<template>
  <transition name="fade" v-if="isBlock1">
    <div class="sales-block-container">
      <div class="sales-block" :class="[typeClass, customClass]" :style="blockStyle">
        <div class="content container">
          <h1 v-if="saleName" class="sale-name light">
            {{ formattedSaleName }}
          </h1>
          <h2 v-if="saleCaption" class="sale-caption">
            {{ saleCaption }}
          </h2>
          <h2 v-if="saleText" class="sale-text">
            {{ saleText }}
          </h2>
          <h2 v-else-if="time && mounted" class="sale-time">
            {{ time }}
          </h2>
        </div>
      </div>
      <h2 v-if="time && mounted" class="sale-time">
        {{ time }}
      </h2>
      <h2 v-else-if="saleText" class="sale-text">
        {{ saleText }}
      </h2>
    </div>
  </transition>
  <transition name="fade" v-else>
    <div class="sales-block" :class="[typeClass, customClass]" :style="blockStyle">
      <div class="content">
        <h1 v-if="saleName" class="sale-name light">
          {{ formattedSaleName }}
        </h1>
        <h2 v-if="saleCaption" class="sale-caption">
          {{ saleCaption }}
        </h2>
        <div v-if="saleText" class="sale-text">
          {{ saleText }}
        </div>
        <div v-else-if="time && mounted" class="sale-time">
          {{ time }}
        </div>
        <div v-if="saleLink" class="sale-link">
          <a :href="saleLink" class="button margin uppercase">Shop the sale</a>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Time from '@article/time';
import timeago from 'timeago.js';
import { computed } from 'vue';
import useLifecycleHook from '@/compositions/useLifecycleHook.js';
import { isPast, endOfDay, parse, parseISO, differenceInCalendarDays } from 'date-fns';

const saleTypes = {
  block1: 'block1',
  block2: 'block2',
  block3: 'block3',
  blockInter1: 'blockInter1',
};

export default {
  props: {
    saleType: {
      type: String,
      required: true,
    },
    saleImage: String,
    saleName: {
      type: String,
      required: true,
    },
    saleCaption: String,
    saleDate: String,
    saleTime: String,
    saleText: String,
    saleLink: String,
    customClass: String,
  },

  setup(props) {
    const { mounted } = useLifecycleHook();

    function getTime(saleTime, saleDate) {
      if(!saleDate) {
        return null;
      }

      const calendarDate = parse(saleDate, 'MM/dd/yyyy', new Date());
      const isoDate = saleTime ? parseISO(saleTime) : null;

      // Is this promotion active after midnight of the final day - this is possible for timezones right of PST
      const activeAfterMidnight = isoDate ? isPast(endOfDay(calendarDate)) && !isPast(isoDate) : false;
      
      if(activeAfterMidnight) {
        return 'This sale ends soon';
      }

      if(isoDate && isPast(isoDate)) {
        return 'This sale has ended';
      }

      // Calendar days remaining on this sale, ignoring exact end time
      const calendarDaysRemaining = differenceInCalendarDays(calendarDate, new Date());

      if(calendarDaysRemaining >= 2 && calendarDaysRemaining <= 7) {
        return `Ends in ${calendarDaysRemaining} days at midnight`;
      }

      if(calendarDaysRemaining === 1) {
        return 'Ends tomorrow at midnight';
      }

      if(calendarDaysRemaining === 0) {
        return 'Ends tonight at midnight';
      }

      const calenderSaleEndDate = Time.renderDate(saleDate, 'MD');
      return `Ends ${calenderSaleEndDate} at midnight`;
    }

    const time = computed(() => getTime(props.saleTime, props.saleDate));

    const typeClass = computed(() => {
      if (props.saleType != null && saleTypes[props.saleType] != null) {
        return props.saleType;
      }
      return '';
    });

    const blockStyle = computed(() => {
      if (
        (saleTypes[props.saleType] == 'block1' || saleTypes[props.saleType] == 'block3' || saleTypes[props.saleType] == 'blockInter1') &&
        props.saleImage
      ) {
        return {
          background: `url(${props.saleImage})`,
          backgroundSize: 'cover',
        };
      } else {
        return {};
      }
    });

    const formattedSaleName = computed(() => {
      return props.saleName;
    });
    const isBlock1 = computed(() => {
      return props.saleType == saleTypes.block1;
    });

    return {
      blockStyle,
      time,
      typeClass,
      isBlock1,
      formattedSaleName,
      mounted
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.fade-enter-active {
  transition: opacity 250ms ease-out;
}

.fade-enter {
  opacity: 0;
}

.sales-block-container {
  & > .sale-time,
  & > .sale-text {
    width: 90%;
    margin: 16px auto 0px;
    text-align: center;
    text-transform: none;
    font-size: 0.9em;
    letter-spacing: normal;
  }

  @media (min-width: $mobile-breakpoint) {
    & > .sale-time,
    & > .sale-text {
      display: none;
    }
  }
}

.sales-block {
  box-sizing: border-box;

  &.white {
    color: white;
    .sale-name,
    .sale-caption,
    .sale-time,
    .sale-text {
      color: white;
    }
  }

  &.block1,
  &.block3 {
    height: 242px;
    box-sizing: border-box;
    background: #f8f8f8;
    display: flex;
    align-items: center;

    .content {
      .sale-name,
      .sale-caption {
        font-size: 2.5em;
      }

      .sale-name,
      .sale-caption {
        letter-spacing: 4px;
      }

      .sale-name {
        line-height: 1.2em;
        margin-bottom: 4px;
      }

      .sale-caption {
        font-weight: bold;
        margin-bottom: 4px;
      }

      .sale-text {
        letter-spacing: normal;
      }

      .sale-time,
      .sale-text {
        text-transform: none;
      }

      @media (max-width: calc($mobile-breakpoint - 1px)) {
        .sale-name,
        .sale-caption {
          font-size: 2em;
        }
      }

      @media (min-width: $large-breakpoint) {
        .sale-name,
        .sale-caption {
          font-size: 3em;
        }
      }
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      height: 160px;
      text-align: center;

      .content {
        .sale-name {
          margin-bottom: 0px;
        }

        .sale-time,
        .sale-text {
          display: none;
        }
      }
    }
  }
  &.block3 {
    .content {
      text-align: center;
      .sale-name {
        margin-bottom: 8px;
        letter-spacing: 2px;
      }
      .sale-text {
        max-width: 30em;
        margin: auto;
        line-height: 30px;
        font-size: 1.5em;
      }
    }
  }

  &.block2 {
    text-align: center;
    background: #f8f8f8;
    border-radius: 0 18px 0 0;
    padding: 48px 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    .content {
      .sale-name {
        letter-spacing: 2px;
        margin-bottom: 20px;
        line-height: 1em;

        @media (max-width: calc($mobile-breakpoint - 1px)) {
          font-size: 1em;
        }

        @media (min-width: $mobile-breakpoint) {
          font-size: 1.2em;
        }
      }

      .sale-caption {
        font-weight: bold;
        line-height: 1.2em;
        letter-spacing: 2px;
        margin-bottom: 20px;

        @media (max-width: calc($mobile-breakpoint - 1px)) {
          font-size: 1.9em;
          margin-bottom: 8px;
        }

        @media (min-width: $mobile-breakpoint) {
          font-size: 1.9em;
        }

        @media (min-width: $tablet-breakpoint) {
          font-size: 1.9em;
          line-height: 1em;
        }
      }

      .sale-time,
      .sale-text {
        @media (max-width: calc($mobile-breakpoint - 1px)) {
          font-size: 0.9em;
        }
      }
    }
  }

  &.blockInter1 {
    background: #f8f8f8;
    display: flex;
    justify-content: center;
    padding-top: 15px;
    padding-bottom: 15px;

    .content {
      @mixin containerBase;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      @media (max-width: calc($mobile-breakpoint - 1px)) {
        padding: 24px 0;
        flex-direction: column;
        justify-content: center;
      }

      .sale-name,
      .sale-caption {
        letter-spacing: 2px;
        line-height: 1.2em;

        @media (min-width: $mobile-breakpoint) {
          font-size: 0.9em;
        }

        @media (min-width: $tablet-breakpoint) {
          font-size: 1em;
        }

        @media (min-width: $desktop-breakpoint) {
          font-size: 1.2em;
        }
      }

      .sale-name {
        @media (max-width: calc($mobile-breakpoint - 1px)) {
          margin-bottom: 16px;
          text-align: center;
        }
      }

      .sale-caption {
        font-weight: 800;

        @media (max-width: calc($mobile-breakpoint - 1px)) {
          margin-bottom: 16px;
          text-align: center;
        }

        @media (max-width: calc($tablet-breakpoint - 1px)) {
          justify-self: center;
        }
      }

      .sale-time,
      sale-text {
        font-size: 1em;

        @media (max-width: calc($tablet-breakpoint - 1px)) {
          justify-self: center;
        }

        @media (min-width: $mobile-breakpoint) {
          font-size: 0.8em;
        }

        @media (min-width: $tablet-breakpoint) {
          font-size: 0.9em;
        }

        @media (min-width: $desktop-breakpoint) {
          font-size: 1em;
        }
      }

      .sale-link {
        display: block;
        justify-self: center;
      }
    }
  }

  .sale-caption {
    color: var(--font-color);
  }

  .sale-link {
    display: none;
  }
}
</style>
